/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [ANALYTICS §8] HOW AN EDIT IS WRITTEN INTO AN AUDIT CONTEXT — ONE CONVENTION, TWO READERS.
 *
 *  `audit_logs.context` is a flat `Map` of String (`auditLog.model.ts`), so a diff cannot be a
 *  nested object. It is spelled out as prefixed keys instead:
 *
 *    changed           `name,shortCode`  — every field that moved, in the writer's order
 *    from:<field>      the value before  — only for a field whose value may be DISCLOSED
 *    to:<field>        the value after
 *    added:<field>     `A,B`             — a set change, as codes or short names, sorted
 *    removed:<field>   `C`
 *
 *  The API writes it (`api/src/utils/auditChange.util.ts`) and the Audit log page reads it
 *  (`web/src/components/AuditChange.tsx`), so the key spelling lives HERE and nowhere else — two
 *  literal copies of `'from:'` are the drift that renders a real change as a leftover key.
 *
 *  ⚠️ ZOD-FREE ON PURPOSE. This barrel is the one a budgeted client may import at runtime
 *  (CLAUDE.md §8 [P5b]); nothing here validates, it only names.
 *
 *  ⚠️ A PERSONAL FIELD APPEARS IN `changed` AND NEVER UNDER `from:`/`to:`. An officer's name,
 *  phone, email, service number and photo are the data class `OFFICER_PHONE_REVEALED` keeps out
 *  of this collection: `audit_logs` has a broader read grant than the record it describes.
 *  "Name changed" is attributable; the name itself is not the log's to hold. Which fields are
 *  personal depends on the ENTITY — an org unit's `name` is disclosed, an officer's is not — so it
 *  is the WRITER's `disclose` flag that decides, and the reader renders whatever keys it finds.
 *
 *  ⚠️ `—` MEANS NONE, and it is the same glyph `auditFill` has written for "nobody was there"
 *  since [P4] — one spelling of absence in one collection.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const AUDIT_CHANGED_KEY = 'changed';

/** The glyph for "no value" — an unset description, a root with no parent. */
export const AUDIT_NONE = '—';

/**
 * A value longer than this is cut to one character less plus `…`. A policy description is a
 * thousand characters and a set of thirty short names is longer still; the row records THAT it
 * changed and enough of it to recognise, never a second copy of the document.
 */
export const AUDIT_CONTEXT_VALUE_MAX = 200;

/**
 * ⚠️ THE FIELD VOCABULARY IS A CLOSED TUPLE, SO A LABEL CANNOT BE MISSING.
 *
 * The web's `AUDIT_FIELD_LABEL` is a `Record` over it, and the writers' change lists are typed on
 * it — so a writer that records a new field is a compile error until the page can say its name.
 *
 * ⚠️ NO MEMBER MAY CONTAIN `.` OR START WITH `$`. MEASURED on mongoose 8 (CLAUDE.md §7 [P6b]): such
 * a `Map` key does not round-trip, and the row would be written with a context that reads back
 * short. `auditChange.constant.test.ts` asserts it over every derived key.
 */
export const AUDIT_CHANGE_FIELDS = [
  'name',
  'shortCode',
  'type',
  'subType',
  /* [C5c §11d] An org unit's reach — written by `setOrgUnitElevation` alone. */
  'elevation',
  'parent',
  'phone',
  'email',
  'serviceNumber',
  'photoUrl',
  'accessRole',
  'orgUnit',
  'rank',
  'shortName',
  'displayOrder',
  'eligibleRanks',
  'description',
  'postTypes',
  'vehicleTypes',
  'effectiveFrom',
  'commandRoles',
] as const;
export type AuditChangeField = (typeof AUDIT_CHANGE_FIELDS)[number];

/** The four prefixes, stated once. The key builders below and a READER splitting a key both use it. */
export const AUDIT_CHANGE_PREFIXES = { from: 'from:', to: 'to:', added: 'added:', removed: 'removed:' } as const;

export const auditFromKey = (field: AuditChangeField) => `${AUDIT_CHANGE_PREFIXES.from}${field}` as const;
export const auditToKey = (field: AuditChangeField) => `${AUDIT_CHANGE_PREFIXES.to}${field}` as const;
export const auditAddedKey = (field: AuditChangeField) => `${AUDIT_CHANGE_PREFIXES.added}${field}` as const;
export const auditRemovedKey = (field: AuditChangeField) => `${AUDIT_CHANGE_PREFIXES.removed}${field}` as const;

export const isAuditChangeField = (value: string): value is AuditChangeField =>
  (AUDIT_CHANGE_FIELDS as readonly string[]).includes(value);
